import React, { useCallback, useEffect, useRef } from 'react'
import { Maybe } from '@graphql-tools/utils'
import classNames from 'classnames'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import { ContentInteraction, HOVER_THRESHOLD_MS } from '@/services/Analytics/ContentInteractions'

type BaseHeroBannerProps = {
  recommendationId: string
  backdrop: Maybe<string>
  mobileBackdrop: Maybe<string>
  infoSection: React.ReactElement
  actionHref: string
  altText: string
  interaction?: ContentInteraction | null
  trackInteraction?: (interaction: ContentInteraction, allowAutoImpression?: boolean) => void
}

export const BaseHeroBanner: React.FC<BaseHeroBannerProps> = ({
  recommendationId,
  backdrop,
  mobileBackdrop,
  infoSection,
  actionHref,
  altText,
  interaction,
  trackInteraction,
}) => {
  const [bannerRef, isVisible] = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.9,
  })

  const hoverStartTimeRef = useRef<number | null>(null)
  const impressionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasTrackedImpression = useRef(false)

  const trackClick = useCallback(() => {
    if (interaction && trackInteraction) {
      const pressInteraction = interaction.withPress()
      trackInteraction(pressInteraction)
    }
  }, [interaction, trackInteraction])

  const handleMouseEnter = useCallback(() => {
    hoverStartTimeRef.current = Date.now()
  }, [])

  const handleMouseLeave = useCallback(() => {
    if (!hoverStartTimeRef.current || !interaction || !trackInteraction) return

    const dwellTime = Date.now() - hoverStartTimeRef.current

    if (dwellTime >= HOVER_THRESHOLD_MS) {
      const hoverInteraction = interaction.withHover(dwellTime)
      trackInteraction(hoverInteraction)
    }

    hoverStartTimeRef.current = null
  }, [interaction, trackInteraction])

  // Track impression when visible for at least 1 second
  useEffect(() => {
    if (hasTrackedImpression.current || !interaction || !trackInteraction) return

    if (isVisible) {
      impressionTimeoutRef.current = setTimeout(() => {
        hasTrackedImpression.current = true
        const impressionInteraction = interaction.withImpression()
        trackInteraction(impressionInteraction)
      }, 1000)
    } else {
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
        impressionTimeoutRef.current = null
      }
    }

    return () => {
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
      }
    }
  }, [isVisible, interaction, trackInteraction])

  if (!backdrop || !mobileBackdrop) return null

  return (
    <InternalLink
      href={actionHref}
      className="recommended-banner !cursor-pointer"
      data-recommendation-id={recommendationId}
      onClick={trackClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="px-[2px] md:px-4" ref={bannerRef}>
        <div
          className={classNames(
            'aspect-h-9 aspect-w-6 sm:aspect-w-3 sm:aspect-h-2 lg:aspect-h-1 lg:aspect-w-3 relative w-full rounded-2xl border-2 border-core-gray-800 leading-[0] transition-all duration-500 hover:border-white overflow-hidden',
          )}
        >
          <div className="hidden w-full xl:block se:hidden">
            <Image
              alt={altText}
              className="absolute inset-0 w-full rounded-xl"
              src={`/w_2400,h_800,q_auto,c_thumb,g_north_east/${backdrop}.webp`}
              width={2400}
              height={800}
            />
          </div>
          <div className="hidden lg:block xl:hidden se:hidden">
            <Image
              alt={altText}
              className="absolute inset-0 rounded-xl"
              src={`/w_1800,h_600,q_auto,c_thumb,g_north_east/${backdrop}.webp`}
              width={1800}
              height={600}
            />
          </div>
          <div className="hidden sm:block lg:hidden se:hidden">
            <Image
              alt={altText}
              className="absolute inset-0 rounded-xl"
              src={`/w_900,h_600,q_auto,c_thumb,g_north_east/${backdrop}.webp`}
              width={900}
              height={600}
            />
          </div>
          <div className="sm:hidden">
            <Image
              alt={altText}
              className="absolute inset-0 rounded-xl"
              src={`/w_600,h_900,q_auto,c_fill,g_north/${mobileBackdrop}.webp`}
              width={600}
              height={900}
            />
          </div>
          <div className="absolute bottom-0 mt-auto h-full w-full bg-[linear-gradient(to_bottom,_rgba(0,0,0,0)_0%,_rgba(0,0,0,0)_35%,_rgba(0,0,0,0.6)_60%,_black_85%,_black_100%)] md:hidden" />
          <div className="absolute bottom-0 mt-auto hidden h-full w-full bg-[linear-gradient(to_left,_rgba(0,0,0,0)_0%,_rgba(0,0,0,0)_35%,_rgba(0,0,0,0.6)_60%,rgba(0,0,0,0.8)_85%,_rgba(0,0,0,0.95)_100%)] md:block" />
          {infoSection}
        </div>
      </div>
    </InternalLink>
  )
}
